<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <v-row v-show="!loading">
    <v-col cols="8">
      <!-- Options to resize iframe -->
      <div class="d-flex justify-end">
        <div class="justify-start flex-grow-1" style="max-height: 20px;">
          <div class="mt-1 mb-1">
            <v-select
              v-if="showAgencySelect"
              v-model="workspacePreview"
              class="select-agency"
              :items="agencies"
              :menu-props="{ bottom: true, offsetY: true }"
              color="white"
              label="Agency"
              dense
              outlined
              mandatory
              hide-details
            />
          </div>
        </div>
        <v-btn-toggle
          v-if="allowMarkdownPreview"
          v-model="previewMode"
          class="mx-6 justify-end"
          background-color="#343434"
          mandatory
          dense
        >
          <v-btn class="iframeModes mt-1 mb-1" :value="null">
            <v-icon>mdi-application</v-icon>
          </v-btn>

          <v-btn class="iframeModes mt-1 mb-1" value="MARKDOWN_MODE">
            <v-icon>mdi-language-markdown</v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          v-model="iframeMode"
          class="justify-end"
          mandatory
          background-color="#343434"
          dense
        >
          <v-btn id="iframeModes-phone" class="iframeModes mt-1 mb-1" value="phone" @click="changeIframeMode('phone')">
            <v-icon>phone_iphone</v-icon>
          </v-btn>

          <v-btn id="iframeModes-tablet" class="iframeModes mt-1 mb-1" value="tablet" @click="changeIframeMode('tablet')">
            <v-icon>tablet</v-icon>
          </v-btn>

          <v-btn id="iframeModes-laptop" class="iframeModes mt-1 mb-1" value="laptop" @click="changeIframeMode('laptop')">
            <v-icon>laptop</v-icon>
          </v-btn>
        </v-btn-toggle>
      </div>

      <div style="background-color: rgba(0,0,0,0.3); line-height: 0px;">
        <!--- <div v-show="transparentIframeBugFix" ref="transparentIFrame" class="transparentIFrame"></div> -->
        <iframe id="resp-iframe" class="resp-iframe" :src="previewURL" title="iframe example 1" @load="iframeLoaded"></iframe>
      </div>
    </v-col>
    <v-col v-if="selectedModule.active" cols="4">
      <v-card color="secondary" class="border-radius-0">
        <v-row class="mx-0 grey darken-3">
          <v-card-title>
            <h2>{{ selectedModule.moduleName }}</h2>
          </v-card-title>
        </v-row>
        <GenerateForm
          v-if="!loading"
          class="side-form"
          :fields-to-show="selectedModule.fieldsToShow"
          :raw-content-types="store.state.rawContentTypes"
          :content-valid.sync="contentValidRef"
          :content.sync="content"
          :content-type="newContentType"
          :show-panel-labels="false"
        />
      </v-card>
    </v-col>
  </v-row>
</template>
<script setup>

import gql from 'graphql-tag';

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';

import { useStore } from 'vue2-helpers/vuex';
import { useApolloClient } from '@vue/apollo-composable';
import _ from 'lodash';
import { difference } from '@/utils/content';
import { paths, generateContentQueryMapping, createRelatedContentQueries, createRelatedContentQueriesFromMapping, remapContentToQueryFields } from '@/utils/editor';
import { changeIframeMode } from '@/utils/cms';

const { resolveClient } = useApolloClient();

const store = useStore();
const route = useRoute();

const isSpeedboat = computed(() => store.state.environment.PROJECT === 'speedboat');

const alphaMigrationFields = require('@/configs/alpha-migration-fields.json');

const previewContentTypeQueryMapping = ref(null);

// eslint-disable-next-line no-undef
const props = defineProps({
  contentType: Object,
  contentValid: Boolean,
  content: Object,
  loading: Boolean
});

const iframeMode = ref('laptop');

const sendInformation = info => {
  info.preview = true;
  document.getElementById('resp-iframe').contentWindow.postMessage(info, '*');
  console.log('Send information', info);
};

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:loading', 'update:contentValid', 'update:content']);

const contentValidRef = ref(props.contentValid);

watch(contentValidRef, val => {
  emit('update:contentValid', val);
});

/** ****************************
  PICKERS
**************************** */

const relationContents = ref([]);

const newContentType = ref({ ...props.contentType });

const findContentByUuid = uuid => relationContents.value.find(p => p.uuid === uuid) || null;

const searchRelatedContent = async relations => {
  let relationContentQueries = [];

  // Only search relations that we didn't already retrieved
  const filteredRelations = relations.filter(relation => !findContentByUuid(relation.uuid));

  if (previewContentTypeQueryMapping.value) {
    relationContentQueries = createRelatedContentQueriesFromMapping(filteredRelations, previewContentTypeQueryMapping.value, store.state.rawContentTypes);
  } else {
    relationContentQueries = createRelatedContentQueries(filteredRelations, store.state.rawContentTypes);
  }

  const project = store.state.environment.PROJECT;

  if (relationContentQueries.length) {
    const getPickersQuery = `query ${project}GetPickers { ${relationContentQueries.join(' ')} }`;
    const ApolloClient = resolveClient();

    try {
      const result = await ApolloClient.query({ query: gql(getPickersQuery), fetchPolicy: 'no-cache', errorPolicy: 'ignore', notifyOnNetworkStatusChange: true });

      if (result.data && Object.values(result.data).length) {
        relationContents.value = relationContents.value.concat(Object.values(result.data));
      }
    } catch (error) {
      console.log('[ERROR] getPickersQuery: ', error);
    }
  }
};

const prepareFields = async (content, fields) => {
  // Remove after migration transition
  if (migrationFields.value) {
    content = handleData(content);
    fields = federatedInlineFieldsToLegacy(fields);
  } else {
    content = _.cloneDeep(content);
  }

  const fieldsToSend = {};

  fields.forEach(fieldKey => {
    if (content && (fieldKey in content)) {
      fieldsToSend[fieldKey] = content[fieldKey];
    }
  });

  const contentPaths = paths(fieldsToSend).filter(p => p.includes('.uuid') && !p.includes('parent.'));

  const relations = [];
  contentPaths.forEach(path => {
    const uuid = _.get(content, path);
    const wtype = _.get(content, path.replace('.uuid', '.wtype'));

    // if it has wtype it means it is a contentType and we must get its data
    if (wtype) {
      relations.push({ path, uuid, wtype });
    } else {
      console.log(uuid, ' is not a content type because it does not have wtype associated');
    }
  });

  await searchRelatedContent(relations);

  relations.forEach(r => {
    const relationContent = findContentByUuid(r.uuid);

    if (relationContent) {
      const pathContentToReplace = r.path.replace('.uuid', '');
      // Remove after migration transition
      if (migrationFields.value) {
        _.set(fieldsToSend, pathContentToReplace, handleData(relationContent)); //
      } else {
        _.set(fieldsToSend, pathContentToReplace, relationContent);
      }
    }
  });

  if (previewContentTypeQueryMapping.value) {
    return remapContentToQueryFields(previewContentTypeQueryMapping.value, fieldsToSend);
  }

  return fieldsToSend;
};

/* ********************************************************************** */

const clonedContent = computed(() => JSON.parse(JSON.stringify(props.content)));

const sendIframeFields = async fieldsToBeSent => {
  sendInformation({ loading: true });
  const fields = await prepareFields({ ...props.content }, fieldsToBeSent);
  sendInformation({ fields: fields });
};

watch(
  clonedContent,
  async (newVal, oldVal) => {
    const fieldsToBeSent = Object.keys(difference(newVal, oldVal));

    await sendIframeFields(fieldsToBeSent);
  },
  { deep: true }
);

const selectedModule = ref({
  active: false,
  moduleName: null,
  fieldsToShow: [],
  sectionID: null
});

const iframeLoaded = async () => {
  // ON Alpha we need to send references
  // Request content type query
  sendInformation({ references: store.state.references || {} });

  emit('update:loading', false);

  if (!isSpeedboat.value) {
    const fieldsToBeSent = Object.keys(props.content);

    const fields = await prepareFields(props.content, fieldsToBeSent);
    sendInformation({ fields: fields });
  }
};

// Receives information
const informationReceiver = async event => {
  if (event.data.contentTypeQuery) {
    // Update iframe fields when content type query is received
    previewContentTypeQueryMapping.value = generateContentQueryMapping(event.data.contentTypeQuery, props.contentType, store.state.rawContentTypes);
  }

  // Case user unselected a section
  if (event.data.hasOwnProperty('unselectModule')) {
    console.log('informationReceiver unselectModule');
    selectedModule.value.active = false;
  }

  // In case is inline editing
  if (event.data.hasOwnProperty('inlineFields')) {
    console.log('informationReceiver inlineFields');
    selectedModule.value.active = true;
    selectedModule.value.moduleName = event.data.moduleName;

    if (migrationFields.value) {
      // Remove after migration transition
      selectedModule.value.fieldsToShow = !event.data.inlineFields ? [] : legacyInlineFieldsToFederated(event.data.inlineFields);
    } else {
      selectedModule.value.fieldsToShow = !event.data.inlineFields ? [] : event.data.inlineFields;
    }
  }

  if (event.data.hasOwnProperty('specialInlineFields')) {
    console.log('informationReceiver specialInlineFields');
    newContentType.value.fields = { ...newContentType.value.fields, ...event.data.specialInlineFields };

    const specialInlineFieldsKeys = Object.keys(event.data.specialInlineFields);

    if (migrationFields.value) {
      // Remove after migration transition
      selectedModule.value.fieldsToShow = !specialInlineFieldsKeys ? [] : selectedModule.value.fieldsToShow.concat(legacyInlineFieldsToFederated(specialInlineFieldsKeys));
    } else {
      selectedModule.value.fieldsToShow = !specialInlineFieldsKeys ? [] : selectedModule.value.fieldsToShow.concat(specialInlineFieldsKeys);
    }
  }
};

window.addEventListener('message', informationReceiver, false);

onUnmounted(() => {
  window.removeEventListener('message', informationReceiver, false);
});

const showAgencySelect = computed(() => store.state.environment.PROJECT === 'speedboat' && props.contentType.name === 'Product');

const agencies = [{ text: 'Speedboat', value: '/speedboat' }, { text: 'Sporttrotter', value: '/sporttrotter' }];

const workspacePreview = ref(store.getters.workspace.settings.VUE_APP_PREVIEW ? `/${store.getters.workspace.code}` : '');

const allowMarkdownPreview = ref(props.contentType.configs.markdownPreview);

const previewMode = ref(null);

const previewURL = computed(() => {
  const previewConf = store.getters.workspace.settings.VUE_APP_PREVIEW;

  let type = previewConf.contentType ? `/${route.params.wtype}` : '';

  if (previewMode.value === 'MARKDOWN_MODE') {
    type = '/markdown';
  }

  const uuid = previewConf.uuid ? `/${route.params.uuid}` : '';
  const version = previewConf.version ? `/${store.getters.version.name}` : '';
  const mode = previewConf.mode === 'live' ? '/live' : '/draft';
  let locale = '';

  if (previewConf.locale) {
    if (store.getters.locale) {
      locale = `/${store.getters.locale}`;
    } else {
      locale = `/${previewConf.defaultPreviewLocale}`;
    }
  }

  // Rule preview
  if (props.contentType.name === 'Rule') {
    return `${previewConf.url}/api/rule-preview/${props.content.uuid}`;
  }

  // Menu preview
  if (props.contentType.name === 'Menu') {
    const position = props.content.position || null;
    const site = props.content.siteWorkspace[0] || null;

    if (!position || !site) {
      return previewConf.url;
    }

    return `${previewConf.url}/menu-preview/${site}/${position}/${props.content.uuid}`;
  }

  return `${previewConf.url}/preview${workspacePreview.value}${type}${uuid}${version}${mode}${locale}`;
});

watch(
  previewURL,
  async (newVal, oldVal) => {
    emit('update:loading', true);
  }
);

watch(
  previewContentTypeQueryMapping,
  async (newVal, oldVal) => {
    // Get all fields to send
    const queryFieldsToSend = Object.keys(props.content);

    await sendIframeFields(queryFieldsToSend);
  }
);

/** ******************
 * MIGRATION FROM LEGACY TO FEDERATED TEMPORARY CODOE
 * Remove after migration transition
 * ****************** */

const migrationFields = computed(() => {
  if (store.state.environment.PROJECT === 'alpha') {
    return alphaMigrationFields;
  }

  return null;
});

const legacyInlineFieldsToFederated = inlineFields => {
  if (migrationFields.value && inlineFields.length > 0) {
    return inlineFields.map(inlineField => (migrationFields.value.mapping[route.params.wtype] && migrationFields.value.mapping[route.params.wtype][inlineField]) || inlineField);
  }
};

const federatedInlineFieldsToLegacy = federatedFields => {
  const fields = federatedFields;

  if (migrationFields.value && federatedFields.length > 0) {
    const contentTypeNewSchema = migrationFields.value.mapping[route.params.wtype];

    if (contentTypeNewSchema) {
      federatedFields.forEach((federatedField, index) => {
        Object.entries(contentTypeNewSchema).forEach(([legacyKey, value]) => {
          const federatedKey = typeof value === 'object' ? value.__newName : value;
          if (federatedKey === federatedField) {
            fields[index] = legacyKey;
          }
        });
      });
    }

    return fields;
  }

  return fields;
};

/* ********************************************************************** */

/**
 * Transforms data structure based on a provided schema
 * Remove after migration transition
 */
const remapMigrationDataFields = (dataNewSchema, data) => {
  // console.log("--------------------------------------")
  // console.log("data argument: ", data);
  // console.log("schema: ", contentTypeNewSchema)

  // console.log(data)
  // console.log(contentTypeNewSchema);

  if (!data || data === null) return null;

  Object.entries(dataNewSchema).forEach(([schenmaKey, value]) => {
    //    console.log("oldKey: ", oldKey);
    //   console.log("value: ", value);

    if (Array.isArray(value)) {
      value.forEach(value2 => {
        const dataKey = value2.__newName;

        if (data.hasOwnProperty(dataKey)) {
          const subFieldsSchema = value2;

          if (Array.isArray(data[dataKey])) {
            data[schenmaKey] = [...data[dataKey].map(v => remapMigrationDataFields(subFieldsSchema, v)), ...data[schenmaKey] || []];
          }

          if (dataKey !== schenmaKey) {
            delete data[dataKey];
          }
        }
      });
    } else {
      const dataKey = typeof value === 'object' ? value.__newName : value;

      // If has property
      if (data.hasOwnProperty(dataKey)) {
        if (typeof value === 'object') {
          const subFieldsSchema = value;

          if (Array.isArray(data[dataKey])) {
            data[schenmaKey] = data[dataKey].map(v => remapMigrationDataFields(subFieldsSchema, v));
          } else {
            data[schenmaKey] = remapMigrationDataFields(subFieldsSchema, data[dataKey]);
          }

          if (dataKey !== schenmaKey) {
            delete data[dataKey];
          }
        } else {
          data[schenmaKey] = data[dataKey];
          if (dataKey !== schenmaKey) {
            delete data[dataKey];
          }
        }
      }
    }
  });

  return data;
};

// Remove after migration transition
const handleData = content => {
  const { wtype } = content;

  let convertedContent = JSON.parse(JSON.stringify(content));
  const contentTypeNewSchema = migrationFields.value.mapping[wtype];
  if (contentTypeNewSchema) {
    convertedContent = remapMigrationDataFields(contentTypeNewSchema, convertedContent);
  }

  return convertedContent;
};

</script>

<style scoped>
.select-agency {
  height: 39px;
  width: 150px;
}
</style>
